import { singleton, inject } from "tsyringe";

import type {
    AiTutorSessionChallengeSourceDto,
    AiTutorSessionConceptEnhancementSourceDto,
    AiTutorSessionSourceDto,
    AiTutorSessionStudyReviewSourceDto,
    AiTutorSessionStudySourceDto,
} from "@/entities/ai-tutor-session/domain/dtos";
import { ConceptEnhancementChapterType } from "@/entities/concept-enhancement/domain/enums";
import type { CurriculumDto } from "@/entities/curriculum/domain/dtos";
import type { MiddleUnitDto } from "@/entities/curriculum/domain/dtos";
import type { AiTutorSessionBehaviorStructure } from "@/entities/ai-tutor-session/domain/behaviors/AiTutorSessionBehavior";

// pages
import type { SessionType } from "@/pages/ai-tutor/models/interface";

export interface CurriculumContext {
    curriculumDtos: CurriculumDto[];
    middleUnitDtos: MiddleUnitDto[];
}

export interface BaseSessionNameStrategy<
    T extends AiTutorSessionSourceDto = AiTutorSessionSourceDto,
> {
    canHandle: (source: AiTutorSessionSourceDto) => source is T;
    build: (
        source: T,
        ctx: CurriculumContext,
    ) => {
        sessionType: SessionType;
        sessionName: string;
    };
}

/** studySession */
@singleton()
export class StudySessionNameStrategy
    implements BaseSessionNameStrategy<AiTutorSessionStudySourceDto>
{
    constructor(
        @inject("AiTutorSessionBehavior")
        private readonly aiTutorSessionBehavior: AiTutorSessionBehaviorStructure,
    ) {}

    public canHandle = (
        source: AiTutorSessionSourceDto,
    ): source is AiTutorSessionStudySourceDto => {
        return this.aiTutorSessionBehavior.isStudySource(source);
    };

    public build = (source: AiTutorSessionStudySourceDto, ctx: CurriculumContext) => {
        const matchedCurriculum = ctx.curriculumDtos.find((dto) => dto.id === source.curriculumId);

        // 대단원 이름
        const bigUnitName = matchedCurriculum?.bigUnit.name ?? "···";

        // 학습 섹션 이름
        const studyItemTypeName = matchedCurriculum?.moduleName ?? "···";

        // 문항 번호
        const problemNumber = source.studyProblem.order;

        return {
            sessionType: "기본학습" as SessionType,
            sessionName: `${bigUnitName} ${studyItemTypeName} ${problemNumber}번`,
        };
    };
}

/**
 * studyReviewSession
 */
@singleton()
export class StudyReviewSessionNameStrategy
    implements BaseSessionNameStrategy<AiTutorSessionStudyReviewSourceDto>
{
    constructor(
        @inject("AiTutorSessionBehavior")
        private readonly aiTutorSessionBehavior: AiTutorSessionBehaviorStructure,
    ) {}

    public canHandle = (
        source: AiTutorSessionSourceDto,
    ): source is AiTutorSessionStudyReviewSourceDto => {
        return this.aiTutorSessionBehavior.isStudyReviewSource(source);
    };

    public build = (source: AiTutorSessionStudyReviewSourceDto, _ctx: CurriculumContext) => {
        const problemNumber = source.studyReviewProblem.order;

        return {
            sessionType: "복습하기" as SessionType,
            sessionName: `복습 ${problemNumber}번`,
        };
    };
}

/**
 * challengeSession
 */
@singleton()
export class ChallengeSessionNameStrategy
    implements BaseSessionNameStrategy<AiTutorSessionChallengeSourceDto>
{
    constructor(
        @inject("AiTutorSessionBehavior")
        private readonly aiTutorSessionBehavior: AiTutorSessionBehaviorStructure,
    ) {}

    public canHandle = (
        source: AiTutorSessionSourceDto,
    ): source is AiTutorSessionChallengeSourceDto => {
        return this.aiTutorSessionBehavior.isChallengeSource(source);
    };

    public build = (source: AiTutorSessionChallengeSourceDto, _ctx: CurriculumContext) => {
        const problemNumber = source.challengeProblem.order;

        const challengeTemplateName = source.challengeTemplate.title;

        return {
            sessionType: "챌린지업" as SessionType,
            sessionName: `${challengeTemplateName} ${problemNumber}번`,
        };
    };
}

/**
 * conceptEnhancementSession
 */
@singleton()
export class ConceptEnhancementSessionNameStrategy
    implements BaseSessionNameStrategy<AiTutorSessionConceptEnhancementSourceDto>
{
    constructor(
        @inject("AiTutorSessionBehavior")
        private readonly aiTutorSessionBehavior: AiTutorSessionBehaviorStructure,
    ) {}

    public canHandle = (
        source: AiTutorSessionSourceDto,
    ): source is AiTutorSessionConceptEnhancementSourceDto => {
        return this.aiTutorSessionBehavior.isConceptEnhancementSource(source);
    };

    public build = (source: AiTutorSessionConceptEnhancementSourceDto, ctx: CurriculumContext) => {
        const matchedMiddleUnit = ctx.middleUnitDtos.find(
            (dto) => dto.id === source.conceptEnhancementProblem.middleUnitId,
        );

        const bigUnitName = matchedMiddleUnit?.bigUnit.name ?? "기타";

        const conceptEnhancementChapterType = {
            [ConceptEnhancementChapterType.Content]: "개념 점검하기",
            [ConceptEnhancementChapterType.Test]: "테스트 진행",
            [ConceptEnhancementChapterType.Pretest]: "학습 전 체크",
        };

        const chapterTypeName =
            conceptEnhancementChapterType[source.conceptEnhancementChapter.type];

        const problemNumber = source.conceptEnhancementProblem.order;

        return {
            sessionType: "튼튼개념쌓기" as SessionType,
            sessionName: `${bigUnitName} ${chapterTypeName} ${problemNumber}번`,
        };
    };
}
